import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const HeroSection = () => {
  const heroRef = useRef(null);
  const headlineRef = useRef(null);
  const sublineRef = useRef(null);
  const ctaRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.5 });
    gsap.set([headlineRef.current, sublineRef.current], { opacity: 0, y: 50 });
    if (ctaRef.current) {
      gsap.set(ctaRef.current.children, { opacity: 0, y: 20 });
    }

    tl.to(headlineRef.current,
      { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
    )
      .to(sublineRef.current,
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
        '-=0.5'
      )
      .to(ctaRef.current?.children || [],
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.2, ease: 'power3.out' },
        '-=0.3'
      );
  }, []);

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="/images/hero.webp"
          alt="Pizza Kitchen"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-20 h-20 rounded-full bg-yellow-400/20 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        <div className="absolute bottom-40 right-20 w-16 h-16 rounded-full bg-red-400/20 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/3 right-1/4 w-12 h-12 rounded-full bg-yellow-300/20 animate-bounce" style={{ animationDelay: '0.8s' }}></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-white/90 text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {t.hero.badge}
            </span>
          </div>

          <h1
            ref={headlineRef}
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 ${language === 'mr' ? 'leading-[1.4]' : 'leading-tight'}`}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {t.hero.headline}
          </h1>

          <div
            ref={sublineRef}
            className="flex flex-wrap items-center gap-3 md:gap-6 mb-10"
          >
            {t.hero.subline.split(' • ').map((item, index) => (
              <span
                key={index}
                className="flex items-center gap-2 text-white/90 text-lg"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                {item}
              </span>
            ))}
          </div>

          <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <Button
              onClick={() => scrollToSection('#story')}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-red-800 font-semibold text-base sm:text-lg px-4 sm:px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 w-full sm:w-auto whitespace-nowrap"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Play className="w-5 h-5 mr-2" />
              {t.hero.ctaPrimary}
            </Button>
            <Button
              onClick={() => scrollToSection('#franchise')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-semibold text-base sm:text-lg px-4 sm:px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 w-full sm:w-auto whitespace-nowrap"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {t.hero.ctaSecondary}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 rounded-full border-2 border-white/50 flex items-start justify-center pt-2">
          <div className="w-1 h-3 rounded-full bg-white/80 animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
